'use client'
import { type JSX, useState } from 'react'
import { Button, ButtonProps } from '@/components/ui/deprecated-button'
import { BrowserShareType, useSharePage } from '@/hooks/use-share-page'
import { SalaryType } from '@/lib/generated/graphql-types'
import { useShareMessage } from '@/modules/salary/share/use-share-message'
import { LinkIcon, Share } from 'lucide-react'
import { cn } from '@/lib/css-utils'

interface ShareSalaryV2Props extends ButtonProps {
  salary?: SalaryType
  iconClassName?: string
  textClassName?: string
  isAuthenticated: boolean
  isInPane?: boolean
}

function CopyLink({
  salary,
  iconClassName,
  textClassName,
  isAuthenticated,
  isInPane = false,
  ...rest
}: ShareSalaryV2Props) {
  const { copyLink } = useSharePage()
  const [isCopied, setIsCopied] = useState(false)

  const onShare = async () => {
    await copyLink(window.location.href)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 1500)
  }

  return (
    <Button
      onClick={onShare}
      variant={isInPane ? 'ghost' : 'outline'}
      className={isInPane ? 'px-3' : ''}
      {...rest}
    >
      <LinkIcon className={cn('w-4 h-4 mr-2', iconClassName)} />

      <span className={textClassName}>
        {isCopied ? 'Copied!' : 'Copy Link'}
      </span>
    </Button>
  )
}

function ShareButton({
  salary,
  iconClassName,
  textClassName,
  isAuthenticated,
  isInPane = false,
  ...rest
}: ShareSalaryV2Props) {
  const { shareData } = useSharePage()
  const { defaultMessage } = useShareMessage({
    salary,
    isAuthenticated,
  })

  const onShare = async () => {
    const message = defaultMessage + '\n\n' + window.location.href
    await shareData({ title: 'Marit', text: message })
  }

  return (
    <Button
      onClick={onShare}
      variant={isInPane ? 'ghost' : 'outline'}
      className={isInPane ? 'px-3' : ''}
      {...rest}
    >
      <Share className={cn('w-4 h-4 mr-2', iconClassName)} />
      <span className={textClassName}>Share</span>
    </Button>
  )
}

export function ShareSalaryV2(props: ShareSalaryV2Props): JSX.Element {
  const { browserShareType } = useSharePage()
  const isCopyMode = browserShareType === BrowserShareType.Copy
  if (isCopyMode) {
    return <CopyLink {...props} />
  }
  return <ShareButton {...props} />
}
